import React from 'react'
import { toBitcoin } from 'lib/bsv-util'
import useStore from 'state/knovStore'

export interface BsvPaymentConfirmationItem {
    recipient?: string | null
    address: string
    amountSats: number
}

function shortAddress(address: string): string {
    const trimmedAddress = address.trim()
    return trimmedAddress.length > 16
        ? `${trimmedAddress.slice(0, 8)}...${trimmedAddress.slice(-4)}`
        : trimmedAddress
}

function recipientLabel(recipient: string | null | undefined): string | null {
    if (!recipient) return null
    return recipient.startsWith('@') ? recipient : `@${recipient}`
}

function PaymentConfirmationMessage({ payments }: { payments: BsvPaymentConfirmationItem[] }) {
    const totalSats = payments.reduce((sum, payment) => sum + payment.amountSats, 0)

    return (
        <div style={{ textAlign: 'left' }}>
            <div
                style={{
                    marginBottom: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: 'var(--color-text-secondary)',
                }}
            >
                SEND BSV
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {payments.map((payment, index) => {
                    const recipient = recipientLabel(payment.recipient)
                    return (
                        <div
                            key={`${payment.address}-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '16px',
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '15px', fontWeight: 600 }}>
                                    {recipient || shortAddress(payment.address)}
                                </div>
                                {recipient && (
                                    <div
                                        style={{
                                            marginTop: '2px',
                                            fontSize: '12px',
                                            color: 'var(--color-text-secondary)',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {shortAddress(payment.address)}
                                    </div>
                                )}
                            </div>
                            <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                <div style={{ fontSize: '15px', fontWeight: 700 }}>
                                    {payment.amountSats.toLocaleString()} sats
                                </div>
                                <div
                                    style={{
                                        marginTop: '2px',
                                        fontSize: '12px',
                                        color: 'var(--color-text-secondary)',
                                    }}
                                >
                                    {toBitcoin(payment.amountSats)} BSV
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            {payments.length > 1 && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '16px',
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid var(--color-border-default)',
                        fontWeight: 700,
                    }}
                >
                    <span>Total</span>
                    <span>{totalSats.toLocaleString()} sats</span>
                </div>
            )}
        </div>
    )
}

export function confirmBsvPaymentTray(payments: BsvPaymentConfirmationItem[]): Promise<boolean> {
    return new Promise(resolve => {
        let settled = false
        const resolveOnce = (confirmed: boolean) => {
            if (settled) return
            settled = true
            resolve(confirmed)
        }

        const multiplePayments = payments.length > 1
        useStore
            .getState()
            .actions.trayActions.openConfirmTray(
                <PaymentConfirmationMessage payments={payments} />,
                multiplePayments ? 'Send payments' : 'Send payment',
                () => resolveOnce(true),
                {
                    title: multiplePayments ? 'Confirm payments' : 'Confirm payment',
                    confirmVariant: 'bsv',
                    primaryActionFirst: true,
                    cancelMessage: 'Cancel',
                    onCancel: () => resolveOnce(false),
                },
            )
    })
}
